import {
	Badge,
	Button,
	Card,
	Descriptions,
	Form,
	Input,
	message,
	Modal,
	Result,
	Select,
	Space,
	Spin,
	Table,
	Tabs,
	Typography,
} from 'antd'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import React, { useCallback, useEffect, useState } from 'react'
import {
	getSingleClientAction,
	getWabaManagementDetailsAction,
} from 'slices/singleClient'
import setupWebhook from 'api/clients/setupWebhook'
import registerPhoneNumber from 'api/clients/registerPhoneNumber'
import WabaManagement, { ConnectedWaba } from 'api/clients/wabaManagement'
import getActiveSignalsClients, {
	ActiveSignalsClient,
} from 'api/signals/getActiveSignalsClients'
import activateSignalsForUser from 'api/admin/activateSignalsForUser'
import deactivateSignalsForUser from 'api/signals/deactivateSignalsForUser'
import getAdmins from 'api/admin/getAdmins'
import handleAxiosError from 'utils/handleAxiosError'
import { AxiosError } from 'axios'
import {
	ExclamationCircleOutlined,
	DisconnectOutlined,
	SettingOutlined,
} from '@ant-design/icons'

const { TabPane } = Tabs

interface AdminCandidate {
	_id: string
	firstName?: string
	lastName?: string
	email?: string
	active?: boolean
	belongsTo?: string
	signalsActivated?: boolean
}

interface Props {
	clientId: string
}
const ClientWabaManagement: React.FC<Props> = ({ ...props }) => {
	const { loading, clientProfile, wabaManagementDetails } = useAppSelector(
		a => a.singleClient
	)
	const dispatch = useAppDispatch()
	const [connectedWabas, setConnectedWabas] = useState<ConnectedWaba[]>([])
	const [connectionsLoading, setConnectionsLoading] = useState(false)
	const [deregisteringId, setDeregisteringId] = useState<string | null>(null)
	const [activeSignalsClients, setActiveSignalsClients] = useState<
		ActiveSignalsClient[]
	>([])
	const [signalsLoading, setSignalsLoading] = useState(false)
	const [disconnectingSignalsUserId, setDisconnectingSignalsUserId] = useState<
		string | null
	>(null)
	const [activateModalOpen, setActivateModalOpen] = useState(false)
	const [adminsLoading, setAdminsLoading] = useState(false)
	const [adminCandidates, setAdminCandidates] = useState<AdminCandidate[]>([])
	const [selectedAdminIds, setSelectedAdminIds] = useState<string[]>([])
	const [activatingSignals, setActivatingSignals] = useState(false)
	const [wabaTab, setWabaTab] = useState('wabas')

	React.useEffect(() => {
		dispatch(getWabaManagementDetailsAction(props.clientId))
	}, [props.clientId, dispatch])

	const fetchConnectedWabas = useCallback(async () => {
		if (!props.clientId) return
		setConnectionsLoading(true)
		try {
			const res = await WabaManagement.getConnectedWabas(
				typeof props.clientId === 'string' ? props.clientId : String(props.clientId)
			)
			setConnectedWabas(res.connections || [])
		} catch (e) {
			handleAxiosError(e as AxiosError)
		} finally {
			setConnectionsLoading(false)
		}
	}, [props.clientId])

	useEffect(() => {
		if (props.clientId) fetchConnectedWabas()
	}, [fetchConnectedWabas, props.clientId])

	const fetchActiveSignalsClients = useCallback(async () => {
		if (!props.clientId) return
		setSignalsLoading(true)
		try {
			const list = await getActiveSignalsClients(
				typeof props.clientId === 'string' ? props.clientId : String(props.clientId)
			)
			setActiveSignalsClients(list || [])
		} catch (e) {
			handleAxiosError(e as AxiosError)
			setActiveSignalsClients([])
		} finally {
			setSignalsLoading(false)
		}
	}, [props.clientId])

	useEffect(() => {
		if (props.clientId && wabaTab === 'signals') {
			fetchConnectedWabas()
			fetchActiveSignalsClients()
		}
	}, [fetchActiveSignalsClients, fetchConnectedWabas, props.clientId, wabaTab])

	const loadAdminCandidates = useCallback(async () => {
		if (!props.clientId) return
		setAdminsLoading(true)
		try {
			const res = await getAdmins({ page: 1, size: 500, query: '' })
			const records = (res?.records || []) as unknown as AdminCandidate[]
			const eligible = records.filter(
				admin =>
					String(admin?.belongsTo || '') === String(props.clientId) &&
					admin?.active === true &&
					admin?.signalsActivated !== true
			)
			setAdminCandidates(eligible)
		} catch (e) {
			handleAxiosError(e as AxiosError)
			setAdminCandidates([])
		} finally {
			setAdminsLoading(false)
		}
	}, [props.clientId])

	useEffect(() => {
		if (activateModalOpen) {
			setSelectedAdminIds([])
			loadAdminCandidates()
		}
	}, [activateModalOpen, loadAdminCandidates])

	const [webhookForm] = Form.useForm()
	const [registrationForm] = Form.useForm()
	const [webhookLoader, setWebhookLoader] = useState(false)
	const [registrationLoader, setRegistrationLoader] = useState(false)
	const [resetLoader, setResetLoader] = useState(false)
	const [webhookModalOpen, setWebhookModalOpen] = useState(false)
	const [registrationModalOpen, setRegistrationModalOpen] = useState(false)
	const [coexistenceModalOpen, setCoexistenceModalOpen] = useState(false)
	const [editingCoexistence, setEditingCoexistence] =
		useState<ConnectedWaba | null>(null)
	const [coexistenceForm] = Form.useForm()
	const [coexistenceSaveLoader, setCoexistenceSaveLoader] = useState(false)
	const [coexistenceWebhookLoader, setCoexistenceWebhookLoader] = useState(false)
	const [configuringSignalsUser, setConfiguringSignalsUser] =
		useState<ActiveSignalsClient | null>(null)

	const openWebhookModal = () => {
		setWebhookModalOpen(true)
	}

	useEffect(() => {
		if (webhookModalOpen) {
			webhookForm.setFieldsValue({
				webhookUrl: clientProfile?.webhookUrl || '',
				webhookToken: clientProfile?.webhookToken || '',
			})
		}
	}, [webhookModalOpen, webhookForm, clientProfile])

	const handleSaveWebhook = async (values: {
		webhookUrl: string
		webhookToken: string
	}) => {
		setWebhookLoader(true)
		try {
			await setupWebhook(props.clientId, {
				url: values.webhookUrl,
				webhookToken: values.webhookToken,
			})
			message.success('Webhook updated successfully!')
			setWebhookModalOpen(false)
			dispatch(getWabaManagementDetailsAction(props.clientId))
			dispatch(getSingleClientAction(props.clientId))
		} catch (error) {
			handleAxiosError(error as AxiosError)
		} finally {
			setWebhookLoader(false)
		}
	}

	const openRegistrationModal = () => {
		setRegistrationModalOpen(true)
	}

	useEffect(() => {
		if (registrationModalOpen) {
			registrationForm.setFieldsValue({
				pin: clientProfile?.wa_phone_id?.slice(0, 6) || '',
			})
		}
	}, [registrationModalOpen, registrationForm, clientProfile])

	const handleRegisterNumber = async (values: { pin: string }) => {
		setRegistrationLoader(true)
		try {
			await registerPhoneNumber(props.clientId, values.pin)
			message.success('Number registration initiated.')
			setRegistrationModalOpen(false)
			dispatch(getWabaManagementDetailsAction(props.clientId))
			dispatch(getSingleClientAction(props.clientId))
		} catch (error) {
			handleAxiosError(error as AxiosError)
		} finally {
			setRegistrationLoader(false)
		}
	}

	const handleResetWaba = () => {
		Modal.confirm({
			title: 'Reset WABA Account',
			icon: <ExclamationCircleOutlined />,
			content:
				'This will clear all WABA account details and reset the account to a clean, non-onboarded state. The client will need to restart onboarding. Are you sure you want to continue?',
			okText: 'Reset',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk: async () => {
				setResetLoader(true)
				try {
					await WabaManagement.resetWaba(props.clientId)
					message.success('WABA account reset successfully!')
					dispatch(getWabaManagementDetailsAction(props.clientId))
					dispatch(getSingleClientAction(props.clientId))
					fetchConnectedWabas()
				} catch (error) {
					handleAxiosError(error as AxiosError)
				} finally {
					setResetLoader(false)
				}
			},
		})
	}

	const findConnectionForSignalsUser = useCallback(
		(user: ActiveSignalsClient): ConnectedWaba | null => {
			return (
				connectedWabas.find(conn => {
					if (conn.type !== 'COEXISTENCE') return false
					if (conn.adminId && conn.adminId === user.id) return true
					const connEmail = String(conn.admin?.email || '')
						.trim()
						.toLowerCase()
					const userEmail = String(user.email || '')
						.trim()
						.toLowerCase()
					return Boolean(userEmail && connEmail && connEmail === userEmail)
				}) || null
			)
		},
		[connectedWabas]
	)

	const openCoexistenceConfig = (conn: ConnectedWaba) => {
		setConfiguringSignalsUser(null)
		setEditingCoexistence(conn)
		setCoexistenceModalOpen(true)
	}

	const openSignalsUserConfig = async (user: ActiveSignalsClient) => {
		setConfiguringSignalsUser(user)
		const existing = findConnectionForSignalsUser(user)
		if (existing) {
			setEditingCoexistence(existing)
			setCoexistenceModalOpen(true)
			return
		}

		try {
			const res = await WabaManagement.getCoexistenceConnectionForAdmin(
				props.clientId,
				user.id
			)
			if (res.connection) {
				setEditingCoexistence(res.connection)
				setCoexistenceModalOpen(true)
				return
			}
		} catch {
			// No prior connection — open empty configure form.
		}

		setEditingCoexistence({
			id: '',
			type: 'COEXISTENCE',
			adminId: user.id,
			wa_phone_id: '',
			wabaId: '',
			wa_token: '',
			display_phone_number: '',
			webhookUrl: clientProfile?.webhookUrl || '',
			webhookToken: clientProfile?.webhookToken || '',
			admin: { name: user.name, email: user.email ?? undefined },
			canDeregister: true,
		})
		setCoexistenceModalOpen(true)
	}

	useEffect(() => {
		if (coexistenceModalOpen && editingCoexistence) {
			coexistenceForm.setFieldsValue({
				webhookUrl: editingCoexistence.webhookUrl || '',
				webhookToken: editingCoexistence.webhookToken || '',
				wa_phone_id: editingCoexistence.wa_phone_id || '',
				wabaId: editingCoexistence.wabaId || '',
				wa_token: editingCoexistence.wa_token || '',
				display_phone_number: editingCoexistence.display_phone_number || '',
			})
		}
	}, [coexistenceModalOpen, editingCoexistence, coexistenceForm])

	const handleSaveCoexistenceConnection = async (values: {
		webhookUrl: string
		webhookToken: string
		wa_phone_id: string
		wabaId: string
		wa_token: string
		display_phone_number?: string
	}) => {
		if (!editingCoexistence) return
		setCoexistenceSaveLoader(true)
		try {
			const adminId = editingCoexistence.adminId || configuringSignalsUser?.id
			const useCreateForAdmin =
				Boolean(configuringSignalsUser) || !editingCoexistence.id

			if (useCreateForAdmin) {
				if (!adminId) {
					message.error('Could not determine which Signals user to configure.')
					return
				}
				const result = await WabaManagement.createCoexistenceConnectionForAdmin(
					props.clientId,
					adminId,
					values
				)
				setEditingCoexistence(result.connection)
				message.success('WhatsApp connection saved for Signals user.')
			} else {
				await WabaManagement.updateCoexistenceConnection(
					props.clientId,
					editingCoexistence.id,
					values
				)
				message.success('WhatsApp connection updated successfully!')
			}
			fetchConnectedWabas()
			fetchActiveSignalsClients()
		} catch (error) {
			handleAxiosError(error as AxiosError)
		} finally {
			setCoexistenceSaveLoader(false)
		}
	}

	const handleCoexistenceWebhookSetup = async () => {
		if (!editingCoexistence?.id) {
			message.warning(
				'Save the connection details first, then setup webhook on Meta.'
			)
			return
		}
		try {
			const values = await coexistenceForm.validateFields([
				'webhookUrl',
				'webhookToken',
			])
			setCoexistenceWebhookLoader(true)
			await WabaManagement.setupCoexistenceWebhook(props.clientId, {
				connectionId: editingCoexistence.id,
				url: values.webhookUrl,
				webhookToken: values.webhookToken,
			})
			message.success('Webhook setup on Meta completed!')
			fetchConnectedWabas()
			fetchActiveSignalsClients()
		} catch (error) {
			if ((error as { errorFields?: unknown })?.errorFields) return
			handleAxiosError(error as AxiosError)
		} finally {
			setCoexistenceWebhookLoader(false)
		}
	}

	const handleDeregister = (conn: ConnectedWaba) => {
		const isPrimary = conn.type === 'PRIMARY'
		Modal.confirm({
			title: 'Disconnect WABA',
			icon: <ExclamationCircleOutlined />,
			content: isPrimary
				? 'This will disconnect the primary WhatsApp Business Account. The client will need to re-onboard. Continue?'
				: `Disconnect ${conn.display_phone_number} (${
						conn.admin?.name || 'Coexistence'
				  })?`,
			okText: 'Disconnect',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk: async () => {
				setDeregisteringId(conn.id)
				try {
					await WabaManagement.deregisterWaba({
						clientId: props.clientId,
						...(isPrimary ? { type: 'primary' } : { connectionId: conn.id }),
					})
					message.success('WABA disconnected successfully!')
					fetchConnectedWabas()
					dispatch(getWabaManagementDetailsAction(props.clientId))
					dispatch(getSingleClientAction(props.clientId))
				} catch (error) {
					handleAxiosError(error as AxiosError)
				} finally {
					setDeregisteringId(null)
				}
			},
		})
	}

	const handleActivateSignals = useCallback(async () => {
		if (selectedAdminIds.length === 0) {
			message.warning('Please select at least one user')
			return
		}
		setActivatingSignals(true)
		try {
			await Promise.all(
				selectedAdminIds.map(adminId =>
					activateSignalsForUser(props.clientId, adminId)
				)
			)
			message.success(
				selectedAdminIds.length === 1
					? 'Signals activated for user successfully'
					: `Signals activated for ${selectedAdminIds.length} users successfully`
			)
			setActivateModalOpen(false)
			setSelectedAdminIds([])
			fetchActiveSignalsClients()
		} catch (e) {
			handleAxiosError(e as AxiosError)
		} finally {
			setActivatingSignals(false)
		}
	}, [fetchActiveSignalsClients, props.clientId, selectedAdminIds])

	const handleDisconnectSignalsUser = useCallback(
		(user: ActiveSignalsClient) => {
			Modal.confirm({
				title: 'Disconnect Signals user',
				icon: <ExclamationCircleOutlined />,
				content: `Disconnect ${user.name} from Signals?`,
				okText: 'Disconnect',
				okType: 'danger',
				cancelText: 'Cancel',
				onOk: async () => {
					setDisconnectingSignalsUserId(user.id)
					try {
						const conn = findConnectionForSignalsUser(user)
						if (conn) {
							await WabaManagement.deregisterWaba({
								clientId: props.clientId,
								connectionId: conn.id,
							})
						}
						await deactivateSignalsForUser(props.clientId, user.id)
						message.success('Signals user disconnected successfully')
						fetchConnectedWabas()
						fetchActiveSignalsClients()
					} catch (e) {
						handleAxiosError(e as AxiosError)
					} finally {
						setDisconnectingSignalsUserId(null)
					}
				},
			})
		},
		[
			fetchActiveSignalsClients,
			fetchConnectedWabas,
			findConnectionForSignalsUser,
			props.clientId,
		]
	)

	const { status } = wabaManagementDetails || {}
	const isDisconnected = status === 'DISCONNECTED'

	let coexistenceModalTitle = 'Coexistence WABA'
	if (configuringSignalsUser) {
		coexistenceModalTitle = `WhatsApp API — ${configuringSignalsUser.name}`
	} else if (editingCoexistence) {
		coexistenceModalTitle = `Coexistence WABA — ${
			editingCoexistence.admin?.name ||
			editingCoexistence.admin?.email ||
			editingCoexistence.display_phone_number
		}`
	}

	return (
		<Space direction='vertical' size='large' style={{ width: '100%' }}>
			<Card title='Connected WABAs'>
				<Tabs activeKey={wabaTab} onChange={setWabaTab}>
					<TabPane tab='WABAs' key='wabas'>
						<Spin spinning={connectionsLoading}>
							{connectedWabas.length > 0 ? (
								<Table
									dataSource={connectedWabas}
									rowKey='id'
									pagination={false}
									columns={[
										{
											title: 'Type',
											dataIndex: 'type',
											key: 'type',
											render: (t: string) => (
												<Badge
													status={t === 'PRIMARY' ? 'processing' : 'default'}
													text={t}
												/>
											),
										},
										{
											title: 'Phone Number',
											dataIndex: 'display_phone_number',
											key: 'display_phone_number',
										},
										{
											title: 'WABA ID',
											dataIndex: 'wabaId',
											key: 'wabaId',
											ellipsis: true,
										},
										{
											title: 'Connected Agent',
											dataIndex: ['admin', 'name'],
											key: 'admin',
											render: (_: unknown, record: ConnectedWaba) =>
												record.admin?.name || record.admin?.email || '-',
										},
										{
											title: 'Action',
											key: 'action',
											render: (_: unknown, record: ConnectedWaba) => (
												<Space size='small'>
													{record.type === 'COEXISTENCE' && (
														<Button
															type='link'
															size='small'
															icon={<SettingOutlined />}
															onClick={() => openCoexistenceConfig(record)}
														>
															Configure
														</Button>
													)}
													<Button
														type='link'
														danger
														size='small'
														icon={<DisconnectOutlined />}
														loading={deregisteringId === record.id}
														onClick={() => handleDeregister(record)}
													>
														Disconnect
													</Button>
												</Space>
											),
										},
									]}
								/>
							) : (
								<Typography.Text type='secondary'>
									No connected WABAs. Primary WABA will appear here after onboarding.
									Coexistence (secondary) WABAs will also appear here when connected from
									the CRM.
								</Typography.Text>
							)}
						</Spin>
					</TabPane>
					<TabPane tab='Signals' key='signals'>
						<div style={{ marginBottom: 12, textAlign: 'right' }}>
							<Button type='primary' onClick={() => setActivateModalOpen(true)}>
								Activate users on Signals
							</Button>
						</div>
						<Spin spinning={signalsLoading}>
							<Table
								rowKey='id'
								pagination={false}
								dataSource={activeSignalsClients}
								locale={{ emptyText: 'No users active on Signals yet.' }}
								columns={[
									{
										title: 'User',
										key: 'name',
										render: (_: unknown, record: ActiveSignalsClient) => (
											<div>
												<div>{record.name}</div>
												<Typography.Text type='secondary'>
													{record.mobileDisplay || '-'}
												</Typography.Text>
											</div>
										),
									},
									{
										title: 'Signals App',
										key: 'signalsAppConnected',
										render: (_: unknown, record: ActiveSignalsClient) => (
											<Badge
												status={record.signalsAppConnected ? 'success' : 'default'}
												text={record.signalsAppConnected ? 'Connected' : 'Disconnected'}
											/>
										),
									},
									{
										title: 'Connected WABA',
										key: 'waBusinessPhone',
										render: (_: unknown, record: ActiveSignalsClient) =>
											record.waBusinessPhone || '-',
									},
									{
										title: 'Action',
										key: 'action',
										render: (_: unknown, record: ActiveSignalsClient) => (
											<Space size='small'>
												<Button
													type='link'
													size='small'
													icon={<SettingOutlined />}
													onClick={() => openSignalsUserConfig(record)}
												>
													Configure
												</Button>
												<Button
													type='link'
													danger
													size='small'
													icon={<DisconnectOutlined />}
													loading={disconnectingSignalsUserId === record.id}
													onClick={() => handleDisconnectSignalsUser(record)}
												>
													Disconnect
												</Button>
											</Space>
										),
									},
								]}
							/>
						</Spin>
					</TabPane>
				</Tabs>
			</Card>

			<Modal
				title='Activate users on Signals'
				visible={activateModalOpen}
				onCancel={() => {
					setActivateModalOpen(false)
					setSelectedAdminIds([])
				}}
				onOk={handleActivateSignals}
				okText='Activate'
				cancelText='Cancel'
				confirmLoading={activatingSignals}
			>
				<Select
					mode='multiple'
					placeholder='Select users'
					value={selectedAdminIds}
					onChange={setSelectedAdminIds}
					loading={adminsLoading}
					style={{ width: '100%' }}
					options={adminCandidates.map(admin => ({
						value: admin._id,
						label: `${
							[admin.firstName, admin.lastName].filter(Boolean).join(' ').trim() ||
							admin.email ||
							admin._id
						}${admin.email ? ` (${admin.email})` : ''}`,
					}))}
					optionFilterProp='label'
					showSearch
				/>
			</Modal>

			<Modal
				title={coexistenceModalTitle}
				visible={coexistenceModalOpen}
				onCancel={() => {
					setCoexistenceModalOpen(false)
					setEditingCoexistence(null)
					setConfiguringSignalsUser(null)
				}}
				maskClosable={false}
				destroyOnClose
				width={640}
				footer={[
					<Button
						key='cancel'
						onClick={() => {
							setCoexistenceModalOpen(false)
							setEditingCoexistence(null)
							setConfiguringSignalsUser(null)
						}}
						disabled={coexistenceSaveLoader || coexistenceWebhookLoader}
					>
						Cancel
					</Button>,
					<Button
						key='webhook'
						onClick={handleCoexistenceWebhookSetup}
						loading={coexistenceWebhookLoader}
						disabled={coexistenceSaveLoader}
					>
						Setup Webhook on Meta
					</Button>,
					<Button
						key='submit'
						type='primary'
						loading={coexistenceSaveLoader}
						disabled={coexistenceWebhookLoader}
						onClick={() => coexistenceForm.submit()}
					>
						Save
					</Button>,
				]}
			>
				<Typography.Paragraph type='secondary'>
					Configure webhook and WhatsApp Cloud API credentials for this Signals
					user&apos;s coexistence connection. Paste values from staging, save, then
					use &quot;Setup Webhook on Meta&quot; to register the production webhook.
				</Typography.Paragraph>
				<Form
					form={coexistenceForm}
					layout='vertical'
					onFinish={handleSaveCoexistenceConnection}
				>
					<Typography.Title level={5}>Webhook</Typography.Title>
					<Form.Item
						label='Webhook URL'
						name='webhookUrl'
						rules={[{ required: true, message: 'Enter Webhook URL' }]}
					>
						<Input placeholder='https://...' />
					</Form.Item>
					<Form.Item
						label='Webhook Token'
						name='webhookToken'
						rules={[{ required: true, message: 'Enter Webhook Token' }]}
					>
						<Input placeholder='Verify token' />
					</Form.Item>
					<Typography.Title level={5} style={{ marginTop: 16 }}>
						WhatsApp Cloud API
					</Typography.Title>
					<Form.Item
						label='Phone Number ID'
						name='wa_phone_id'
						rules={[{ required: true, message: 'Enter Phone Number ID' }]}
					>
						<Input />
					</Form.Item>
					<Form.Item
						label='WABA ID'
						name='wabaId'
						rules={[{ required: true, message: 'Enter WABA ID' }]}
					>
						<Input />
					</Form.Item>
					<Form.Item
						label='Access Token'
						name='wa_token'
						rules={[{ required: true, message: 'Enter Access Token' }]}
					>
						<Input.TextArea rows={3} />
					</Form.Item>
					<Form.Item label='Display Phone Number' name='display_phone_number'>
						<Input placeholder='e.g. 919876543210' />
					</Form.Item>
				</Form>
			</Modal>

			<Card
				title='Reset WABA Account'
				extra={
					<Button danger loading={resetLoader} onClick={handleResetWaba}>
						Reset WABA
					</Button>
				}
			>
				<Typography.Text type='secondary'>
					This will clear all WABA account details and reset the account to a clean,
					non-onboarded state. The client will need to restart onboarding.
				</Typography.Text>
			</Card>

			{(() => {
				if (wabaManagementDetails) {
					return (
						<Card
							title='Primary WABA Details'
							extra={
								<Space>
									<Button onClick={openWebhookModal}>Webhook Setup</Button>
									{isDisconnected && (
										<Button type='primary' onClick={openRegistrationModal}>
											Number Registration
										</Button>
									)}
								</Space>
							}
						>
							<Spin spinning={loading}>
								<Descriptions bordered column={3}>
									<Descriptions.Item label='Webhook' span={3}>
										<Typography.Text copyable>
											{wabaManagementDetails.webhook_configuration
												.whatsapp_business_account || ''}
										</Typography.Text>
									</Descriptions.Item>
									<Descriptions.Item label='Name Status'>
										{wabaManagementDetails.name_status}
									</Descriptions.Item>
									<Descriptions.Item label='Phone Number status'>
										<Badge
											status={isDisconnected ? 'error' : 'success'}
											text={wabaManagementDetails.status}
										/>
									</Descriptions.Item>
									<Descriptions.Item label='Whatsapp Connected Number'>
										{wabaManagementDetails.display_phone_number}
									</Descriptions.Item>
									<Descriptions.Item label='Quality Rating' span={1}>
										{wabaManagementDetails.quality_rating || ''}
									</Descriptions.Item>
									<Descriptions.Item label='Verified Name' span={2}>
										<Badge
											status={
												wabaManagementDetails.name_status === 'APPROVED'
													? 'success'
													: 'warning'
											}
											text={wabaManagementDetails.verified_name}
										/>
									</Descriptions.Item>
									<Descriptions.Item label='Throughput' span={3}>
										{wabaManagementDetails.throughput.level}
									</Descriptions.Item>
								</Descriptions>
							</Spin>

							<Modal
								title='Webhook Setup'
								visible={webhookModalOpen}
								onCancel={() => setWebhookModalOpen(false)}
								maskClosable={false}
								destroyOnClose
								footer={[
									<Button
										key='cancel'
										onClick={() => setWebhookModalOpen(false)}
										disabled={webhookLoader}
									>
										Cancel
									</Button>,
									<Button
										key='submit'
										type='primary'
										loading={webhookLoader}
										onClick={() => webhookForm.submit()}
									>
										Save
									</Button>,
								]}
							>
								<Form form={webhookForm} layout='vertical' onFinish={handleSaveWebhook}>
									<Form.Item
										label='Webhook URL'
										name='webhookUrl'
										rules={[{ required: true, message: 'Enter Webhook URL' }]}
									>
										<Input placeholder='Enter Webhook URL' />
									</Form.Item>
									<Form.Item
										label='Webhook Token'
										name='webhookToken'
										rules={[{ required: true, message: 'Enter Webhook Token' }]}
									>
										<Input placeholder='Enter Webhook Token' />
									</Form.Item>
								</Form>
							</Modal>

							<Modal
								title='Number Registration'
								visible={registrationModalOpen}
								onCancel={() => setRegistrationModalOpen(false)}
								maskClosable={false}
								destroyOnClose
								footer={[
									<Button
										key='cancel'
										onClick={() => setRegistrationModalOpen(false)}
										disabled={registrationLoader}
									>
										Cancel
									</Button>,
									<Button
										key='submit'
										type='primary'
										loading={registrationLoader}
										onClick={() => registrationForm.submit()}
									>
										Register
									</Button>,
								]}
							>
								<Form
									form={registrationForm}
									layout='vertical'
									onFinish={handleRegisterNumber}
								>
									<Form.Item
										label='PIN (6 digits)'
										name='pin'
										rules={[{ required: true, message: 'Enter PIN' }]}
									>
										<Input placeholder='Enter PIN' maxLength={6} />
									</Form.Item>
								</Form>
							</Modal>
						</Card>
					)
				}
				if (connectedWabas.length > 0) {
					return (
						<Card title='Primary WABA'>
							<Typography.Text type='secondary'>
								Primary WABA is not connected. Secondary WABAs (coexistence) are listed
								above. Connect the primary channel via onboarding to enable full
								messaging.
							</Typography.Text>
						</Card>
					)
				}
				return (
					<Result
						title='No WABA Details Found'
						subTitle='Connect primary WABA via onboarding, or add coexistence (secondary) WABAs from the CRM.'
					/>
				)
			})()}
		</Space>
	)
}
export default ClientWabaManagement
